import { PropsWithChildren, ReactNode } from "react"
import { countryCodeToName } from "@oysterhr/common"
import { staffUserPath } from "@oysterhr/router"
import { FlagIcon, Avatar, Badge, BadgeVariation, Text } from "@oysterhr/ui"
import { Link } from "react-router-dom"
import classnames from "classnames"
import { Actor, Maybe, TerminationUser, User } from "@oysterhr/schema"
import DOMPurify from "dompurify"
import type { InfoFormLabelProps } from "~/shared/components/InfoFormLabel/types"
import { InfoFormLabel } from "~/shared/components/InfoFormLabel"
import { DateTime } from "luxon"
import {
  currencyInputSelectOptions,
  IconWithTooltip,
  useSubdivisionName,
} from "@oysterhr/feature-components"

type BooleanFieldProps = InfoFormLabelProps & {
  value?: Maybe<boolean>
  dataTestId?: string
  assistiveText?: InfoFormLabelProps["assistiveText"]
  valueTooltip?: ReactNode
}

export const BooleanField = ({
  valueTooltip,
  label,
  value,
  assistiveText,
  dataTestId,
}: BooleanFieldProps) => (
  <TextField
    infoTooltip={assistiveText}
    dataTestId={dataTestId}
    label={label}
    value={
      <div className="flex gap-2">
        <Text as="span">{value == null ? null : value ? "Yes" : "No"}</Text>
        {valueTooltip && (
          <IconWithTooltip unsafe_isValueInnerHTML value={valueTooltip} icon="InfoCircle" />
        )}
      </div>
    }
  />
)

export type TextFieldProps = InfoFormLabelProps & {
  value?: Maybe<string> | Maybe<number> | ReactNode
  dataTestId?: string
  placeholder?: string
}

export const TextField = ({
  label,
  value,
  isOptional,
  manualEditBlocked,
  assistiveText,
  dataTestId,
  children,
  placeholder = "Unknown",
  endAdornment,
  disabled,
}: PropsWithChildren<TextFieldProps>) => {
  const valueIsEmpty = value == null || value === "" || value === undefined
  return (
    <div data-testid={dataTestId}>
      <InfoFormLabel
        label={label}
        isOptional={isOptional}
        manualEditBlocked={manualEditBlocked}
        infoTooltip={assistiveText}
        endAdornment={endAdornment}
        disabled={disabled}
        className="text-sm font-normal"
      />
      <div className="mb-4 mt-1">
        <div
          className={classnames("text-base font-medium", {
            "mb-1": !!children,
            "text-gray-300": valueIsEmpty,
          })}
        >
          {valueIsEmpty ? placeholder : value}
        </div>
        {children}
      </div>
    </div>
  )
}

type CheckboxGroupFieldProps = InfoFormLabelProps & {
  answers: Array<string>
  choices: Array<string>
  dataTestId?: string
}

export const CheckboxGroupField = ({
  label,
  answers,
  choices,
  isOptional,
  assistiveText,
  dataTestId,
}: CheckboxGroupFieldProps) => {
  return (
    <div data-testid={dataTestId}>
      <InfoFormLabel
        label={label}
        isOptional={isOptional}
        infoTooltip={assistiveText}
        className="text-sm font-normal"
      />
      {choices?.map((choice, index) => (
        <CheckboxField answers={answers} choice={choice} key={`${dataTestId}-${index}`} />
      ))}
    </div>
  )
}

type CheckboxFieldProps = InfoFormLabelProps & {
  answers?: Array<string>
  choice: string
  key: string
}

const CheckboxField = ({ answers, choice, key }: CheckboxFieldProps) => {
  const isChecked = answers?.includes(choice)
  if (isChecked) {
    return (
      <div className={classnames("mb-4 mt-1 space-x-2 font-medium text-gray-400")} key={key}>
        <input type="checkbox" checked />
        <label>{choice}</label>
      </div>
    )
  }
  return (
    <div className={classnames("mb-4 mt-1 space-x-2 font-normal text-gray-300")} key={key}>
      <input type="checkbox" disabled />
      <label>{choice}</label>
    </div>
  )
}

type BigTextFieldProps = InfoFormLabelProps & {
  value?: Maybe<string>
}

export const BigTextField = ({ label, value, isOptional, assistiveText }: BigTextFieldProps) => {
  return (
    <div className="col-span-2">
      <InfoFormLabel
        label={label}
        isOptional={isOptional}
        infoTooltip={assistiveText}
        className="text-sm font-normal"
      />
      <div
        className={classnames("mb-4 mt-1 text-base font-medium", {
          "text-gray-300": value == null,
        })}
      >
        <pre className="font-body" style={{ width: "32rem", whiteSpace: "pre-wrap" }}>
          {value == null || value === "" ? "Not entered" : value}
        </pre>
      </div>
    </div>
  )
}

interface PillFieldProps {
  label: string
  variation: BadgeVariation
  value: string
}

export const PillField = ({ label, value, variation }: PillFieldProps) => {
  return (
    <div>
      <div className="text-xs font-normal">{label}</div>
      <div className="mb-4 mt-2">
        <Badge variation={variation}>{value}</Badge>
      </div>
    </div>
  )
}

interface UserFieldProps {
  label: string
  user?: Maybe<Pick<User | TerminationUser, "id" | "avatarUrl" | "name" | "email">>
  showEmail?: boolean
  description?: string
}

export const UserField = ({ label, user, showEmail, description }: UserFieldProps) => {
  return (
    <div>
      <div className="text-xs font-normal">{label}</div>
      {description && <div className="text-xs font-normal text-gray-400">{description}</div>}
      <div className="mb-4 mt-2">
        {user && (
          <Link to={staffUserPath(user.id || "")} className=" text-gray-400 ">
            <div className="flex items-center justify-start">
              <div className="pr-2">
                <Avatar src={user.avatarUrl || undefined} name={user.name || undefined} size="sm" />
              </div>
              <div>
                <div className="font-medium underline">{user.name}</div>
                {showEmail && <div className="text-xs">{user.email}</div>}
              </div>
            </div>
          </Link>
        )}
        {!user && <div className="mb-4 mt-1  font-normal italic text-gray-300">Unknown</div>}
      </div>
    </div>
  )
}

interface ActorFieldProps {
  actor: Actor
}

// Like UserField but does not need to be a User
// Very much coupled with GQL's ActorType
export const ActorField = ({ actor }: ActorFieldProps) => {
  const avatarPart = (
    <div className="flex items-center justify-start">
      <div className="pr-2">
        <Avatar src={actor.avatarUrl || undefined} name={actor.name || undefined} size="sm" />
      </div>
      <div>{actor.name}</div>
    </div>
  )

  const wrapInALink = actor.type === "USER"

  return (
    <>
      {actor && actor.id && wrapInALink && (
        <Link to={staffUserPath(actor.id)} className="font-medium text-gray-400 underline">
          {avatarPart}
        </Link>
      )}
      {actor && !wrapInALink && avatarPart}
      {!actor && <div className="mb-4 mt-1  font-normal italic text-gray-300">Unknown</div>}
    </>
  )
}

type LabelProps = PropsWithChildren<{
  label: string
}>

export const Label = ({ label, children }: LabelProps) => {
  return (
    <div>
      <div className="text-xs font-normal">{label}</div>
      <div className="mb-4 mt-2">{children}</div>
    </div>
  )
}

const currencyOptionGroups = currencyInputSelectOptions()
const currencyOptions = [
  ...currencyOptionGroups[0].options,
  ...(currencyOptionGroups[1]?.options || []),
]

type CurrencyFieldProps = InfoFormLabelProps & {
  value?: Maybe<string>
  dataTestId?: string
}

export const CurrencyField = ({
  label,
  value,
  dataTestId,
  isOptional,
  assistiveText,
  endAdornment,
  className,
}: PropsWithChildren<CurrencyFieldProps>) => {
  const currencyOption = currencyOptions.find(option => option.currencyCode === value)

  return (
    <div data-testid={dataTestId}>
      <InfoFormLabel
        infoTooltip={endAdornment}
        label={label}
        isOptional={isOptional}
        assistiveText={assistiveText}
        className="text-sm font-normal"
      />
      <div className={classnames("mb-4 mt-1 flex justify-start", className)}>
        <div>
          <FlagIcon countryCode={currencyOption?.countryCode || undefined} />
        </div>
        <div
          className={classnames("text-base font-medium", {
            "text-gray-300": value == null,
          })}
        >
          {value ? <div className="pl-2">{value}</div> : "Unknown"}
        </div>
      </div>
    </div>
  )
}

type CountryFieldProps = InfoFormLabelProps & {
  value?: Maybe<string>
  dataTestId?: string
}

export const CountryField = ({
  label,
  value,
  dataTestId,
  isOptional,
  assistiveText,
  endAdornment,
  className,
}: CountryFieldProps) => {
  return (
    <div data-testid={dataTestId}>
      <InfoFormLabel
        infoTooltip={endAdornment}
        label={label}
        isOptional={isOptional}
        assistiveText={assistiveText}
        className="text-sm font-normal"
      />
      <div className={classnames("mb-4 mt-1 flex justify-start", className)}>
        <div>
          <FlagIcon countryCode={value || undefined} />
        </div>
        <div
          className={classnames("text-base font-medium", {
            "text-gray-300": value == null,
          })}
        >
          {value ? <div className="pl-2">{countryCodeToName(value)}</div> : "Unknown"}
        </div>
      </div>
    </div>
  )
}

type SubdivisionFieldProps = InfoFormLabelProps & {
  subdivisionCode?: Maybe<string>
  countryCode?: Maybe<string>
  dataTestId?: string
}
export const SubdivisionField = ({
  label,
  subdivisionCode,
  countryCode,
  dataTestId,
  isOptional,
  assistiveText,
}: SubdivisionFieldProps) => {
  const subdivisionName = useSubdivisionName(countryCode || "", subdivisionCode || "")
  return (
    <div data-testid={dataTestId}>
      <InfoFormLabel
        label={label}
        isOptional={isOptional}
        infoTooltip={assistiveText}
        className="text-sm font-normal"
      />
      <div className="mb-4 mt-1 flex justify-start">
        <div
          className={classnames({
            "font-medium text-gray-400": subdivisionCode != null,
            "font-normal italic text-gray-300": subdivisionCode == null,
          })}
        >
          {subdivisionName ?? "Unknown"}
        </div>
      </div>
    </div>
  )
}

type DateFieldProps = InfoFormLabelProps & {
  dateString?: string
  dataTestId?: string
}

export const DateField = ({
  label,
  dateString,
  dataTestId,
  isOptional,
  assistiveText,
  endAdornment,
}: DateFieldProps) => (
  <TextField
    label={label}
    value={dateString ? DateTime.fromISO(dateString).toLocaleString(DateTime.DATE_FULL) : null}
    dataTestId={dataTestId}
    isOptional={isOptional}
    infoTooltip={assistiveText}
    endAdornment={endAdornment}
  />
)

export type FileType = {
  downloadUrl?: {
    url: string
  }
  title: string
  description?: Maybe<string>
  filename?: Maybe<string>
}

type FileFieldProps = InfoFormLabelProps & {
  dataTestId?: string
  file?: FileType
  onClick?: (file: FileType) => void
}

export const FileField = ({
  file,
  label,
  dataTestId,
  isOptional,
  assistiveText,
  onClick,
}: FileFieldProps) => {
  const renderFile = () => {
    if (onClick && file) {
      return (
        // eslint-disable-next-line jsx-a11y/anchor-is-valid
        <a
          className="cursor-pointer"
          onClick={() => onClick(file)}
          onKeyDown={() => onClick(file)}
          role="button"
          tabIndex={0}
        >
          <span className="font-medium text-gray-400 underline">{file.title}</span>
        </a>
      )
    } else if (file) {
      /* It's possible that some documents don't have a url exposed, so we need to be able to show them */
      return file.downloadUrl?.url ? (
        <a href={file.downloadUrl.url} target="_blank">
          <span className="font-medium text-gray-400 underline">{file.title}</span>
        </a>
      ) : (
        <span className="font-medium text-gray-400 underline">{file.title}</span>
      )
    }
    return <span className="font-normal italic text-gray-300">No document uploaded</span>
  }

  return (
    <div data-testid={dataTestId}>
      {label && (
        <InfoFormLabel
          label={label}
          isOptional={isOptional}
          infoTooltip={assistiveText}
          className="text-sm font-normal"
        />
      )}
      <div className={classnames("mb-4 mt-1")}>{renderFile()}</div>
    </div>
  )
}

type RichTextFieldProps = InfoFormLabelProps & {
  value?: Maybe<string>
  placeholder?: string
  className?: string
  labelClassName?: string
}

export const RichTextField = ({
  label,
  value,
  isOptional,
  manualEditBlocked,
  assistiveText,
  placeholder = "Not entered",
  className = "col-span-2",
}: RichTextFieldProps) => {
  return (
    <div className={className}>
      <InfoFormLabel
        label={label}
        isOptional={isOptional}
        manualEditBlocked={manualEditBlocked}
        infoTooltip={assistiveText}
        className="text-sm font-normal"
      />
      <div
        className={classnames(
          "mb-4 mt-1 max-h-64 overflow-auto rounded-sm border border-gray-200 p-2",
          {
            "font-medium text-gray-400": value != null,
            "font-normal italic text-gray-300": value == null,
          },
        )}
      >
        <pre
          className="font-body text-sm font-normal"
          style={{ whiteSpace: "pre-wrap" }}
          // Sanitized
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={
            value ? { __html: DOMPurify.sanitize(value) } : { __html: placeholder }
          }
        ></pre>
      </div>
    </div>
  )
}
